import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import SuperAdminNav from '../components/SuperAdminNav';

const PROMO_TYPES = [
  { label: 'Free Trial', value: 'free_trial' },
  { label: 'Discount', value: 'discount' },
  { label: 'Coupon', value: 'coupon' },
];

const emptyForm = {
  name: '',
  type: 'free_trial',
  duration_days: 14,
  stripe_coupon_id: '',
  discount_description: '',
  status: 'active',
  start_date: '',
  end_date: '',
  max_redemptions: '',
};

export default function SuperPromos() {
  const { apiFetch } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [viewRedemptions, setViewRedemptions] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  const loadPromos = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/promos');
      setPromos(data.promos);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadPromos(); }, [loadPromos]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm({
      name: promo.name || '',
      type: promo.type || 'free_trial',
      duration_days: promo.duration_days || 14,
      stripe_coupon_id: promo.stripe_coupon_id || '',
      discount_description: promo.discount_description || '',
      status: promo.status || 'active',
      start_date: promo.start_date ? promo.start_date.split('T')[0] : '',
      end_date: promo.end_date ? promo.end_date.split('T')[0] : '',
      max_redemptions: promo.max_redemptions || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        duration_days: form.type === 'free_trial' ? (parseInt(form.duration_days) || 14) : null,
        stripe_coupon_id: form.type !== 'free_trial' ? form.stripe_coupon_id : null,
        discount_description: form.type !== 'free_trial' ? form.discount_description : null,
      };
      if (editing) {
        await apiFetch(`/api/super/promos/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/super/promos', { method: 'POST', body: JSON.stringify(body) });
      }
      setShowModal(false);
      loadPromos();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete promo "${promo.name}"?`)) return;
    try {
      await apiFetch(`/api/super/promos/${promo.id}`, { method: 'DELETE' });
      loadPromos();
    } catch (err) { alert(err.message); }
  };

  const showRedemptions = async (promo) => {
    setViewRedemptions(promo);
    setLoadingRedemptions(true);
    try {
      const data = await apiFetch(`/api/super/promos/${promo.id}/redemptions`);
      setRedemptions(data.redemptions);
    } catch (err) { console.error(err); }
    finally { setLoadingRedemptions(false); }
  };

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString();
  };

  const typeLabel = (type) => {
    const t = PROMO_TYPES.find(p => p.value === type);
    return t ? t.label : type;
  };

  return (
    <div>
      <SuperAdminNav activeItem="promos" />
      <div className="admin-page" style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Manage Promos</h1>
          <button className="btn btn-orange" onClick={openAdd}>+ New Promo</button>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : promos.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No promos yet. Create one to get started.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {promos.map(promo => (
              <div key={promo.id} className="card" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: '0.95rem' }}>{promo.name}</strong>
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                        background: promo.status === 'active' ? 'rgba(46,204,113,0.15)' : 'rgba(243,156,18,0.15)',
                        color: promo.status === 'active' ? '#2ecc71' : '#f39c12',
                      }}>{promo.status}</span>
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                        background: 'rgba(155,89,182,0.15)', color: '#9b59b6',
                      }}>{typeLabel(promo.type)}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {promo.type === 'free_trial' && `${promo.duration_days} days`}
                      {promo.type !== 'free_trial' && promo.discount_description}
                      {' | '}{formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                      {promo.max_redemptions && ` | Max: ${promo.max_redemptions}`}
                      {' | '}{promo.redemption_count} redeemed
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => showRedemptions(promo)}>Redemptions</button>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(promo)}>Edit</button>
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(promo)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 16 }}>{editing ? 'Edit Promo' : 'New Promo'}</h2>

            <div className="form-group">
              <label className="form-label">Promo Name</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. January Free Trial" />
            </div>

            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                {PROMO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {form.type === 'free_trial' && (
              <div className="form-group">
                <label className="form-label">Duration (days)</label>
                <input className="form-input" type="number" min="1" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} />
              </div>
            )}

            {form.type !== 'free_trial' && (
              <>
                <div className="form-group">
                  <label className="form-label">Stripe Coupon ID</label>
                  <input className="form-input" value={form.stripe_coupon_id} onChange={e => setForm({ ...form, stripe_coupon_id: e.target.value })} placeholder="e.g. promo_abc123" />
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Description</label>
                  <input className="form-input" value={form.discount_description} onChange={e => setForm({ ...form, discount_description: e.target.value })} placeholder="e.g. 50% off for 3 months" />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Max Redemptions</label>
                <input className="form-input" type="number" min="1" value={form.max_redemptions} onChange={e => setForm({ ...form, max_redemptions: e.target.value })} placeholder="Unlimited" />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-orange" onClick={handleSave} disabled={saving || !form.name} style={{ flex: 1 }}>
                {saving ? 'Saving...' : (editing ? 'Update Promo' : 'Create Promo')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Redemptions Modal */}
      {viewRedemptions && (
        <div className="modal-overlay" onClick={() => setViewRedemptions(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Redemptions: {viewRedemptions.name}</h2>
              <button className="btn btn-sm btn-outline" onClick={() => setViewRedemptions(null)}>Close</button>
            </div>

            {loadingRedemptions ? (
              <div className="loading"><div className="spinner" /></div>
            ) : redemptions.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>No redemptions yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {redemptions.map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--card-border)' }}>
                    <div>
                      <strong style={{ fontSize: '0.9rem' }}>{r.first_name} {r.last_name}</strong>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>{r.email}</span>
                      <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, marginLeft: 8, background: 'rgba(52,152,219,0.15)', color: '#3498db', fontWeight: 600 }}>{r.user_type}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                      <div>{new Date(r.redeemed_at).toLocaleDateString()}</div>
                      {r.trial_end && <div style={{ fontSize: '0.7rem' }}>Expires {new Date(r.trial_end).toLocaleDateString()}</div>}
                      <span style={{
                        fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                        background: r.status === 'active' ? 'rgba(46,204,113,0.15)' : 'rgba(231,76,60,0.15)',
                        color: r.status === 'active' ? '#2ecc71' : '#e74c3c',
                      }}>{r.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
